// =====================================================================================
// INVRT Radar - web console - the person journey timeline (story C8)
//
// The person-level counterpart of the company journey timeline (CompanyJourneySection,
// V03): the contact's page views rendered as an ordered vertical timeline, reusing the
// V03 visual conventions (the ordered list, the timestamp header with the clock mark, the
// honest empty state). RULED (Desk 10 Oct item 6): page views ONLY. The wire's ContactPage
// carries url, title and viewed_at and that is the whole surface - no conversion moments
// and no event capture here; that is a later, separate platform story.
//
// Every entry is a page the wire actually carries. The timeline orders oldest first (the
// company journey's convention) using the timestamps the wire itself carries, never drops
// a row, and shows the honest empty state when nothing was recorded - a contact with no
// page views gets words, never invented rows.
// =====================================================================================

import { Clock3, ExternalLink } from "lucide-react";
import { EmptyState } from "@/components/States";
import { formatDateTime } from "@/lib/format";
import type { ContactPage } from "@/lib/types.c1";

export interface PersonJourneyTimelineProps {
  /**
   * The wire's page views (GET /contacts/:id), url/title/viewed_at. `undefined` is passed
   * explicitly for a read that carries no pages at all (a revealed person's record) and
   * renders the same honest empty state as an empty list.
   */
  pages?: ContactPage[] | undefined;
}

/**
 * Oldest first, by the timestamps the wire carries. Array.prototype.sort is stable, so
 * rows sharing a timestamp (and rows with no usable timestamp, pushed to the end) keep the
 * wire's own order: the timeline orders, it never reorders or drops.
 */
function orderedOldestFirst(pages: ContactPage[]): ContactPage[] {
  const timestampOf = (page: ContactPage): number => {
    if (!page.viewed_at) return Number.POSITIVE_INFINITY;
    const parsed = Date.parse(page.viewed_at);
    return Number.isNaN(parsed) ? Number.POSITIVE_INFINITY : parsed;
  };
  return [...pages].sort((a, b) => timestampOf(a) - timestampOf(b));
}

export function PersonJourneyTimeline({ pages }: PersonJourneyTimelineProps) {
  if (!pages || pages.length === 0) {
    return (
      <EmptyState
        title="No page views were recorded"
        body="When this person's page views arrive they will build up here, oldest first. Nothing is invented in the meantime."
      />
    );
  }

  const ordered = orderedOldestFirst(pages);

  return (
    <div className="flex flex-col gap-2">
      <p className="text-xs text-ink-muted" data-testid="person-page-view-summary">
        {ordered.length} {ordered.length === 1 ? "page view" : "page views"} - oldest first. Page
        views only: conversion moments are not captured on this timeline.
      </p>
      <ol
        aria-label="Person page view timeline"
        className="flex flex-col gap-2"
        data-testid="person-page-view-timeline"
      >
        {ordered.map((page, index) => (
          <li
            key={`${index}-${page.url ?? ""}`}
            className="flex flex-col gap-1 rounded-card border border-line px-4 py-3"
          >
            <div className="flex flex-wrap items-center gap-2">
              <span className="inline-flex items-center gap-1.5 text-sm font-medium text-ink">
                <Clock3 className="h-3.5 w-3.5 text-ink-faint" aria-hidden="true" />
                {formatDateTime(page.viewed_at)}
              </span>
            </div>
            {page.url ? (
              <a
                href={page.url}
                target="_blank"
                rel="noreferrer noopener"
                className="inline-flex items-center gap-1 text-primary underline-offset-2 hover:underline"
              >
                {page.title ?? page.url}
                <ExternalLink className="h-3 w-3" aria-hidden="true" />
                {/* Story O9: the new-tab warning rides in the accessible name, so a screen
                    reader hears it before activating the link. */}
                <span className="sr-only">(opens in a new tab)</span>
              </a>
            ) : (
              <span className="text-ink">{page.title ?? "-"}</span>
            )}
            {page.url && page.title ? (
              <span className="break-all text-xs text-ink-muted">{page.url}</span>
            ) : null}
          </li>
        ))}
      </ol>
    </div>
  );
}
